import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
	Header,
	Burger,
	Menu,
	ActionIcon,
	Switch,
	Box,
	createStyles,
} from '@mantine/core'
import { useResizeObserver } from '@mantine/hooks'
import DotsVertical from 'tabler-icons-react/dist/icons/dots-vertical'
import LogoutIcon from 'tabler-icons-react/dist/icons/logout'
import { logout } from '../services/actions/authActions'
import BrandLogo from './BrandLogo'
import AccountSettingsCard from './AccountSettingsCard'
import WorkspaceContext from './WorkspaceContext'
import useWorkspaceContext from '../hooks/useWorkspaceContext'

const HEADER_CONTENT_MIN_HEIGHT = 72

const useStyles = createStyles((theme) => ({
	content: {
		display: 'grid',
		gridTemplateColumns: 'calc(var(--mantine-navbar-width, 250px) - 32px) minmax(0, 1fr) 44px',
		alignItems: 'center',
		gap: theme.spacing.md,
		padding: '12px 16px',
		minHeight: HEADER_CONTENT_MIN_HEIGHT,
		[theme.fn.smallerThan('sm')]: {
			gridTemplateColumns: '44px minmax(0, 1fr) 44px',
			columnGap: theme.spacing.xs,
			padding: '8px 12px',
		},
	},
	burger: {
		display: 'none',
		[theme.fn.smallerThan('sm')]: { display: 'block', gridColumn: 1, gridRow: 1 },
	},
	brand: {
		minWidth: 0,
		[theme.fn.smallerThan('sm')]: { display: 'none' },
	},
	context: {
		minWidth: 0,
		[theme.fn.smallerThan('sm')]: { gridColumn: 2, gridRow: 1 },
	},
	menu: {
		justifySelf: 'end',
		[theme.fn.smallerThan('sm')]: { gridColumn: 3, gridRow: 1 },
	},
}))

const MainNav = ({
	opened,
	toggle,
	setShowOrgModal,
	setShowExpModal,
	colorScheme,
	toggleColorScheme,
}) => {
	const navigate = useNavigate()
	const { classes } = useStyles()
	const [headerRef, headerRect] = useResizeObserver()
	const context = useWorkspaceContext()
	const { hasSwitchableOrg, showExperienceSwitcher } = context
	const userType = useSelector((state) => state.user.authUser?.userType)
	const authUser = useSelector((state) => state.user.authUser)

	return (
		<Header height={headerRect.height ? Math.ceil(headerRect.height) + 1 : HEADER_CONTENT_MIN_HEIGHT + 1} p={0}>
			<div ref={headerRef}>
				<Box className={classes.content}>
					<Box className={classes.burger}>
						<Burger
							opened={opened}
							onClick={toggle}
							size='sm'
							color='gray'
							aria-label={opened ? 'Close navigation' : 'Open navigation'}
							aria-expanded={opened}
							sx={{ width: 44, height: 44 }}
						/>
					</Box>

					<Box className={classes.brand}>
						<BrandLogo height={26} scheme={colorScheme} />
					</Box>
					<Box className={classes.context}>
						<WorkspaceContext context={context} />
					</Box>

					<Box className={classes.menu}>
						<Menu position='bottom-end' shadow='md' width={280}>
							<Menu.Target>
								<ActionIcon
									variant='subtle'
									aria-label='Workspace menu'
									size={44}
									color={colorScheme === 'dark' ? 'gray' : 'dark'}
								>
									<DotsVertical size={20} />
								</ActionIcon>
							</Menu.Target>
							<Menu.Dropdown>
								{hasSwitchableOrg && (
									<Menu.Item onClick={() => setShowOrgModal(true)}>
										Switch Organization
									</Menu.Item>
								)}
								{showExperienceSwitcher && (
									<Menu.Item onClick={() => setShowExpModal(true)}>
										Switch Experience
									</Menu.Item>
								)}
								{userType === 'superAdmin' && (
									<Menu.Item onClick={() => navigate(`/control/orgs`)}>
										Control Orgs
									</Menu.Item>
								)}
								<Menu.Item
									closeMenuOnClick={false}
									onClick={toggleColorScheme}
									rightSection={
										<div onClick={(event) => event.stopPropagation()}>
											<Switch
												size='xs'
												checked={colorScheme === 'dark'}
												onChange={toggleColorScheme}
												aria-label='Toggle dark mode'
											/>
										</div>
									}
								>
									Dark Mode
								</Menu.Item>
								<Box px='xs' py='xs'>
									<AccountSettingsCard
										name={authUser?.name}
										email={authUser?.email}
										fullWidth
										onClick={() => navigate('/account/settings')}
									/>
								</Box>
								<Menu.Item
									color='red'
									icon={<LogoutIcon size={16} />}
									onClick={logout}
								>
									Logout
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					</Box>
				</Box>
			</div>
		</Header>
	)
}

export default MainNav
